import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import type { Role } from '@nlinks/shared';
import { useAuth } from '@/store/auth';
import { AppShell } from '@/components/layout/AppShell';
import { Spinner } from '@/components/ui';
import { LoginPage } from '@/pages/LoginPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { OnboardingPage } from '@/pages/OnboardingPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { DayDetailPage } from '@/pages/DayDetailPage';
import { MailboxesPage } from '@/pages/MailboxesPage';
import { KeywordsPage } from '@/pages/KeywordsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AddUserPage } from '@/pages/admin/AddUserPage';
import { ViewUsersPage } from '@/pages/admin/ViewUsersPage';
import { SuperUsersPage } from '@/pages/super/SuperUsersPage';
import { SuperAdminsPage } from '@/pages/super/SuperAdminsPage';
import { SuperMailsPage } from '@/pages/super/SuperMailsPage';

function FullPageSpinner() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <Spinner className="h-8 w-8" />
    </div>
  );
}

/** Where each role lands after signing in. */
export function homeFor(role: Role): string {
  if (role === 'SUPER_ADMIN') return '/super/users';
  if (role === 'ADMIN') return '/admin/users';
  return '/';
}

/**
 * Gate for every authenticated route.
 *
 * The two mandatory setup steps are enforced here in order — change password,
 * then connect a mailbox — but only for end users. Administrators have no
 * mailboxes of their own, so onboarding does not apply to them.
 *
 * The API independently rejects every data route until the password has been
 * changed. Onboarding cannot be enforced server-side — completing it means
 * creating the first mailbox, which is one of those routes — so that step is
 * sequenced here.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, initializing } = useAuth();
  const location = useLocation();

  if (initializing) return <FullPageSpinner />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  const isStaff = user.role === 'ADMIN' || user.role === 'SUPER_ADMIN';
  if (
    !isStaff &&
    !user.mustChangePassword &&
    !user.onboardingCompleted &&
    location.pathname !== '/onboarding'
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}

function RequireRole({ roles, children }: { roles: Role[]; children: ReactNode }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} replace />;
  return <>{children}</>;
}

/** Sends "/" to whichever landing page suits the signed-in role. */
function RoleHome() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'USER') return <DashboardPage />;
  return <Navigate to={homeFor(user.role)} replace />;
}

export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route
        path="/change-password"
        element={
          <RequireAuth>
            <ChangePasswordPage />
          </RequireAuth>
        }
      />
      {/*
        Role-guarded as well as sequenced. Onboarding means connecting a
        mailbox, which administrators do not have — without the guard, typing
        the URL drops them into a setup flow that can never complete.
      */}
      <Route
        path="/onboarding"
        element={
          <RequireAuth>
            <RequireRole roles={['USER']}>
              <OnboardingPage />
            </RequireRole>
          </RequireAuth>
        }
      />

      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* ── end user ── */}
        <Route path="/" element={<RoleHome />} />
        <Route
          path="/days/:day"
          element={
            <RequireRole roles={['USER']}>
              <DayDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/mailboxes"
          element={
            <RequireRole roles={['USER']}>
              <MailboxesPage />
            </RequireRole>
          }
        />
        <Route
          path="/keywords"
          element={
            <RequireRole roles={['USER']}>
              <KeywordsPage />
            </RequireRole>
          }
        />
        <Route path="/settings" element={<SettingsPage />} />

        {/* ── admin ── */}
        <Route
          path="/admin/add-user"
          element={
            <RequireRole roles={['ADMIN', 'SUPER_ADMIN']}>
              <AddUserPage />
            </RequireRole>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireRole roles={['ADMIN', 'SUPER_ADMIN']}>
              <ViewUsersPage />
            </RequireRole>
          }
        />

        {/* ── super admin ── */}
        <Route
          path="/super/users"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <SuperUsersPage />
            </RequireRole>
          }
        />
        <Route
          path="/super/admins"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <SuperAdminsPage />
            </RequireRole>
          }
        />
        <Route
          path="/super/mails"
          element={
            <RequireRole roles={['SUPER_ADMIN']}>
              <SuperMailsPage />
            </RequireRole>
          }
        />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
